import React, { useState } from "react";
import styled from "styled-components";
import { FiAward, FiChevronDown, FiChevronUp } from "react-icons/fi";
import CertificateCard from "./CertificateCard";
import CertificateViewer from "./CertificateViewer";
import { certificates, categories } from "./certificateData.js";

const Certificates = () => {
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const filtered = certificates.filter(
    (certificate) => category === "All" || certificate.category === category,
  );
  const visible = expanded ? filtered : filtered.slice(0, 6);

  return (
    <Container id="certificates" aria-labelledby="certificates-heading">
      <Content>
        <Heading>
          <div>
            <Eyebrow>Certificates</Eyebrow>
            <h2 id="certificates-heading">
              Always learning.
              <br />
              <span>Putting it into practice.</span>
            </h2>
            <Description>
              A collection of assessments and conference attendance across
              software engineering, frontend development, and the tools I work with.
            </Description>
          </div>
          <Summary>
            <FiAward aria-hidden="true" />
            <div>
              <strong>{certificates.length} certificates</strong>
            </div>
          </Summary>
        </Heading>
        <Filters role="group" aria-label="Filter certificates">
          {categories.map((item) => {
            const count =
              item === "All"
                ? certificates.length
                : certificates.filter(
                    (certificate) => certificate.category === item,
                  ).length;
            return (
              <button
                key={item}
                type="button"
                aria-pressed={category === item}
                onClick={() => {
                  setCategory(item);
                  setExpanded(false);
                }}
              >
                {item}
                <span aria-hidden="true">{count}</span>
              </button>
            );
          })}
        </Filters>
        <Results role="status">
          Showing {visible.length} of {filtered.length} certificates
          {category !== "All" ? ` · ${category}` : ""}
        </Results>
        <Grid>
          {visible.map((certificate, index) => (
            <CertificateCard
              key={certificate.id}
              certificate={certificate}
              onOpen={() => setActiveIndex(index)}
            />
          ))}
        </Grid>
        {filtered.length > 6 && (
          <More>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded
                ? "Show fewer certificates"
                : `View all ${filtered.length} certificates`}{" "}
              {expanded ? (
                <FiChevronUp aria-hidden="true" />
              ) : (
                <FiChevronDown aria-hidden="true" />
              )}
            </button>
          </More>
        )}
        <ClosingNote>
          <FiAward aria-hidden="true" />
          <span>Open any certificate to take a closer look.</span>
        </ClosingNote>
      </Content>
      {activeIndex !== null && (
        <CertificateViewer
          certificates={filtered}
          activeIndex={activeIndex}
          onNavigate={setActiveIndex}
          onClose={() => setActiveIndex(null)}
        />
      )}
    </Container>
  );
};

export default Certificates;

const Container = styled.section`
  padding: 3.5rem 1rem;
  background:
    radial-gradient(ellipse at 100% 0%, #e3f0e7, transparent 50%), #f5f8f4;
  color: #253d31;
  @media (min-width: 600px) {
    padding: 5rem 5%;
  }
  @media (min-width: 1024px) {
    padding-block: 6rem;
  }
`;
const Content = styled.div`
  max-width: 1120px;
  margin-inline: auto;
`;
const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  h2 {
    font-size: clamp(1.9rem, 4vw, 3.25rem);
    font-weight: 500;
    letter-spacing: -0.055em;
    line-height: 1.15;
  }
  h2 span {
    color: #008369;
  }
  @media (min-width: 960px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    gap: 3rem;
  }
`;
const Eyebrow = styled.p`
  margin-bottom: 1.25rem;
  color: #476259;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;
const Description = styled.p`
  max-width: 52ch;
  margin-top: 1.25rem;
  color: #596b60;
  font-size: 0.9rem;
  line-height: 1.85;
`;
const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.25rem;
  border: 1px solid #d5e5d9;
  border-radius: 1rem;
  background: #ffffff80;
  > svg {
    flex-shrink: 0;
    font-size: 1.75rem;
    color: #008369;
  }
  strong {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
  }
  span {
    display: block;
    margin-top: 0.35rem;
    color: #65736b;
    font-size: 0.65rem;
    line-height: 1.6;
  }
  @media (min-width: 960px) {
    flex-shrink: 0;
  }
`;
const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 2.5rem;
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 44px;
    padding: 0.6rem 0.9rem;
    border: 1px solid #d5e3d9;
    border-radius: 999px;
    background: #ffffffb3;
    color: #47594f;
    font-size: 0.75rem;
    cursor: pointer;
    span {
      display: grid;
      place-items: center;
      min-width: 1.3rem;
      height: 1.3rem;
      border-radius: 50%;
      background: #eaf1ec;
      color: #47594f;
      font-size: 0.6rem;
    }
    &[aria-pressed="true"] {
      background: #183c32;
      border-color: #183c32;
      color: #fff;
    }
    &[aria-pressed="true"] span {
      background: #ffffff20;
      color: #d1f3df;
    }
    &:hover:not([aria-pressed="true"]) {
      background: #e7f0e9;
    }
    &:focus-visible {
      outline: 2px solid #008369;
      outline-offset: 3px;
    }
  }
  @media (max-width: 420px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    button {
      padding-inline: 0.5rem;
    }
  }
`;
const Results = styled.p`
  margin: 1.25rem 0;
  color: #65736b;
  font-size: 0.7rem;
  line-height: 1.6;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;
const More = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    min-height: 48px;
    padding: 0.8rem 1.5rem;
    border: 1px solid #183c32;
    border-radius: 999px;
    background: #183c32;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    &:hover {
      background: #006e57;
    }
    &:focus-visible {
      outline: 2px solid #008369;
      outline-offset: 4px;
    }
  }
`;
const ClosingNote = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  color: #65736b;
  font-size: 0.7rem;
  line-height: 1.7;
  text-align: center;
  svg {
    flex-shrink: 0;
  }
`;
